import { useState, useEffect, useCallback } from 'react';
import { refusalMessage } from '../../../lib/chatRefusal';
import { ChatSession } from '../types';

const API_URL = import.meta.env.VITE_API_URL || '';

export function useChatSessions() {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSessions = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_URL}/healthcare-chat-v2/sessions`, {
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to fetch sessions');

      const data = await response.json();
      setSessions(data.sessions || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sessions');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const createSession = useCallback(
    async (title?: string) => {
      try {
        const response = await fetch(`${API_URL}/healthcare-chat-v2/sessions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ title }),
        });

        if (!response.ok) {
          // Same reasoning as the send path: a refusal carries a reason worth showing.
          if (response.status === 403) {
            setError(await refusalMessage(response));
            return null;
          }
          throw new Error('Failed to create session');
        }

        const newSession = await response.json();
        await fetchSessions();
        return newSession;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create session');
        return null;
      }
    },
    [fetchSessions],
  );

  const deleteSession = useCallback(async (sessionId: number) => {
    try {
      const response = await fetch(`${API_URL}/healthcare-chat-v2/sessions/${sessionId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        // Same reasoning as the send path: a refusal carries a reason worth showing.
        if (response.status === 403) {
          setError(await refusalMessage(response));
          return null;
        }
        throw new Error('Failed to delete session');
      }

      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete session');
      return false;
    }
  }, []);

  return {
    sessions,
    isLoading,
    error,
    createSession,
    deleteSession,
    refresh: fetchSessions,
  };
}
